// [목적] badge 도메인 zod 스키마 — 배지 row 직렬화 BE/FE 공유
// [이유] folio-social-and-profile Spec WC-DTO-USER-02 — GET /users/me/badges 응답 타입을
//         FE/BE 공유하여 이중 정의 방지. badge code 는 text 로 미리 enum 고정 안 함 (확장성 고려)
// [사이드 이팩트] FE ProfilePage 배지 탭 렌더링에 직접 영향
// [수정 시 고려사항] code 값 변경 시 FE 배지 아이콘 매핑 테이블(badgeIconMap)도 동기 갱신 필요.
//                   meta JSONB 스키마는 코드별로 상이 — FE는 unknown 으로 처리 후 code 분기

import { z } from 'zod';

/**
 * badges 테이블 row 직렬화.
 * kind — 배지 코드 식별자 (text 타입. 예: 'first_folio', 'pioneer', 'gangwon_explorer')
 * meta — 배지별 부가 정보 JSONB (awardCondition, iconUrl 등 코드별 상이)
 */
export const badgeSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  kind: z.string().min(1).max(100),
  awardedAt: z.string(),
  meta: z.record(z.unknown()),
});

export type Badge = z.infer<typeof badgeSchema>;

// ─── 배지 카탈로그 (GET /users/me/badges/catalog) ───────────────────────────────

/**
 * [목적] 전체 배지 도감 1건 — 획득 여부 + 달성 조건 + 진행률
 * [이유] 프로필 뱃지 탭이 "보유 배지만" 보여주면 무엇을 더 모을 수 있는지 알 수 없다.
 *        미획득(잠금) 슬롯을 FE mock 으로 만드는 것은 ADR-T1-5 위반이므로,
 *        BE 가 카탈로그 + 진행률을 내려주고 FE 는 표시만 한다.
 * [사이드 이팩트] tier/category 는 FE 배지 색·정렬에 직결 — 값 추가 시 FE badgeCatalog.ts 동기 필요.
 * [수정 시 고려사항] kind 는 badges.code 와 동일 문자열. progress.target 은 BE 임계 상수의 사본이므로
 *                   임계값을 바꾸면 카탈로그 응답도 자동으로 따라간다(하드코딩 금지).
 */
export const badgeCatalogItemSchema = z.object({
  kind: z.string().min(1).max(100),
  label: z.string().min(1),
  /** 획득 조건 한 줄 설명 (예: "폴리오를 1개 이상 만들면 획득") */
  description: z.string().min(1),
  /** 배지 등급 — FE 메달 색상 매핑 */
  tier: z.enum(['bronze', 'silver', 'gold']),
  /** 배지 묶음 — FE 그룹 헤더/정렬 */
  category: z.enum(['start', 'social', 'theme', 'explore']),
  earned: z.boolean(),
  /** 획득 시각 ISO — 미획득이면 null */
  awardedAt: z.string().nullable(),
  /** 진행률 — current/target (미획득 배지의 게이지). unit 은 "개", "곳" 등 표시 단위 */
  progress: z.object({
    current: z.number().int().min(0),
    target: z.number().int().min(1),
    unit: z.string(),
  }),
});

export type BadgeCatalogItem = z.infer<typeof badgeCatalogItemSchema>;

export const badgeCatalogSchema = z.object({
  items: z.array(badgeCatalogItemSchema),
  earnedCount: z.number().int().min(0),
  totalCount: z.number().int().min(0),
});

export type BadgeCatalog = z.infer<typeof badgeCatalogSchema>;
